import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { STARTUP_API_URL } from "../apiConfig";
import { tenantHeaders } from "../lib/tenantConfig";
import Navbar from "../components/navbar";
import Footer from "../components/Footer";
import AgentChat from "../components/AgentChat";
import { auth } from "../firebase";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Attachment {
  id: number;
  engagement_id: number;
  name: string;
  url: string;
  attachment_type: "link" | "file";
  file_mime?: string;
  created_at: string;
}

interface Engagement {
  id: number;
  startup_id?: number;
  company_name: string;
  sector?: string;
  technology_area?: string;
  startup_stage?: string;
  geography?: string;
  website?: string;
  founders?: string;
  poc_name?: string;
  poc_email?: string;
  poc_phone?: string;
  spoc?: string;
  engagement_stage: string;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  interactions?: Interaction[];
}

interface Interaction {
  id: number;
  engagement_id: number;
  date_of_interaction: string;
  interaction_type: string;
  spoc?: string;
  notes?: string;
  created_at: string;
}

interface EngagementComment {
  id: number;
  user_id: string;
  user_email: string;
  comment: string;
  created_at: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGES = [
  { key: "identified", label: "Identified", color: "bg-gray-500" },
  { key: "connected", label: "Connected", color: "bg-blue-500" },
  { key: "evaluated", label: "Evaluated", color: "bg-yellow-500" },
  { key: "poc", label: "POC", color: "bg-orange-500" },
  { key: "active_collaboration", label: "Active Collaboration", color: "bg-green-500" },
];

const INTERACTION_TYPES = ["Intro Call", "Demo", "Meeting", "Event", "Email", "POC Review", "Follow-up", "Other"];
const SECTORS = ["AI/ML", "Fintech", "Healthcare", "SaaS", "EdTech", "CleanTech", "Cybersecurity", "E-commerce", "IoT", "Blockchain", "Other"];
const GEOGRAPHIES = ["India", "USA", "UK", "Singapore", "UAE", "Germany", "France", "Israel", "Canada", "Australia", "Southeast Asia", "Europe", "Middle East", "Africa", "Latin America", "Global"];
const STARTUP_STAGES = ["Early", "Growth", "Scale"];

const stageColor = (stage: string) =>
  STAGES.find(s => s.key === stage)?.color ?? "bg-gray-600";

const stageLabel = (stage: string) =>
  STAGES.find(s => s.key === stage)?.label ?? stage;

const normalizeStartupStage = (stage: string): string => {
  const s = (stage || "").toLowerCase();
  if (["early", "pre-seed", "pre_seed", "seed", "angel", "grant", "bootstrapped"].some(v => s.includes(v))) return "Early";
  if (["growth", "series_a", "series a", "series_b", "series b"].some(v => s.includes(v))) return "Growth";
  if (["scale", "series_c", "series c", "series_d", "series d", "series_e", "late", "ipo", "public"].some(v => s.includes(v))) return "Scale";
  return stage;
};

// ─── Empty form helpers ───────────────────────────────────────────────────────

const emptyForm = () => ({
  company_name: "",
  sector: "",
  technology_area: "",
  startup_stage: "",
  geography: "",
  website: "",
  founders: "",
  poc_name: "",
  poc_email: "",
  poc_phone: "",
  spoc: "",
  engagement_stage: "identified",
  notes: "",
});

const emptyInteraction = () => ({
  date_of_interaction: new Date().toISOString().slice(0, 16),
  interaction_type: "Intro Call",
  spoc: "",
  notes: "",
});

// ─── Component ────────────────────────────────────────────────────────────────

export default function StartupEngagementRepo() {
  const location = useLocation();
  const navigate = useNavigate();
  const uid = localStorage.getItem("uid") || "";
  const authToken = localStorage.getItem("authToken") || "";
  const userRole = localStorage.getItem("userRole") || "";

  const [tab, setTab] = useState<"list" | "add">("list");
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [filterSector, setFilterSector] = useState("");
  const [filterGeo, setFilterGeo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 7;

  // selected engagement for detail panel
  const [selected, setSelected] = useState<Engagement | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stageView, setStageView] = useState<string>(""); // which stage tab is being viewed in detail
  // checked checklist items per engagement+stage — persisted so they survive tab navigation
  const [checkedItems, setCheckedItems] = useState<Record<string, number[]>>(() => {
    try {
      return JSON.parse(localStorage.getItem("ucdp_engagement_checklist") || "{}");
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("ucdp_engagement_checklist", JSON.stringify(checkedItems));
    } catch {}
  }, [checkedItems]);

  // form
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [aiFilling, setAiFilling] = useState(false);
  const [aiFilledFields, setAiFilledFields] = useState<string[]>([]);

  // interaction form
  const [intForm, setIntForm] = useState(emptyInteraction());
  const [addingInt, setAddingInt] = useState(false);
  const [showIntForm, setShowIntForm] = useState(false);

  // team comments
  const [comments, setComments] = useState<EngagementComment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  // attachments
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showAttachForm, setShowAttachForm] = useState(false);
  const [attachMode, setAttachMode] = useState<"link" | "file">("link");
  const [attachName, setAttachName] = useState("");
  const [attachUrl, setAttachUrl] = useState("");
  const [attachFile, setAttachFile] = useState<File | null>(null);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // toast
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3500);
  };

  const headers = { Authorization: authToken, ...tenantHeaders() };

  // ─── Prefill from Startup Directory "Track this startup" ─────────────────────
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    window.history.replaceState({}, "");
    (async () => {
      try {
        const freshToken = await auth.currentUser?.getIdToken() ?? localStorage.getItem("authToken") ?? "";
        const res = await axios.get(`${STARTUP_API_URL}/api/v1/engagements/check`, {
          params: { company_name: prefill.company_name },
          headers: { Authorization: freshToken, ...tenantHeaders() },
        });
        if (res.data.tracked) {
          showToast(`Already tracking "${prefill.company_name}" — opening existing record`);
          setTab("list");
          if (res.data.data) openDetail(res.data.data as Engagement);
          return;
        }
      } catch { /* if check fails, fall through to normal prefill */ }
      setForm(f => ({
        ...f,
        company_name: prefill.company_name || "",
        sector: prefill.sector || "",
        technology_area: prefill.technology_area || "",
        website: prefill.website || "",
        geography: prefill.geography || "",
        startup_stage: prefill.startup_stage || "",
        founders: prefill.founders || "",
        notes: prefill.notes || "",
      }));
      setEditId(null);
      setTab("add");
    })();
  }, []);

  // ─── Fetch list ─────────────────────────────────────────────────────────────

  const fetchEngagements = async () => {
    setLoading(true);
    try {
      const freshToken = await auth.currentUser?.getIdToken() ?? localStorage.getItem("authToken") ?? "";
      const freshHeaders = { Authorization: freshToken, ...tenantHeaders() };
      const params: Record<string, string> = { uid, role: userRole };
      if (search) params.search = search;
      if (filterStage) params.engagement_stage = filterStage;
      if (filterSector) params.sector = filterSector;
      if (filterGeo) params.geography = filterGeo;

      const res = await axios.get(`${STARTUP_API_URL}/api/v1/engagements/`, { headers: freshHeaders, params });
      if (res.data.status === "success") setEngagements(res.data.data);
    } catch {
      showToast("Failed to load engagements", false);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search — covers initial load, typing, and filter changes
  useEffect(() => {
    setCurrentPage(1);
    const timer = setTimeout(() => fetchEngagements(), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search, filterStage, filterSector, filterGeo]);

  // ─── Open detail ─────────────────────────────────────────────────────────────

  const fetchComments = async (engId: number) => {
    try {
      const res = await axios.get(`${STARTUP_API_URL}/api/v1/engagements/${engId}/comments`, { headers });
      if (res.data.status === "success") setComments(res.data.data);
    } catch { setComments([]); }
  };

  const handlePostComment = async () => {
    if (!newComment.trim() || !selected) return;
    setPostingComment(true);
    try {
      const userEmail = auth.currentUser?.email || localStorage.getItem("userEmail") || localStorage.getItem("uid") || uid;
      const res = await axios.post(`${STARTUP_API_URL}/api/v1/engagements/${selected.id}/comments`, {
        user_id: uid,
        user_email: userEmail,
        comment: newComment.trim(),
      }, { headers });
      if (res.data.status === "success") {
        setComments(prev => [...prev, res.data.data]);
        setNewComment("");
      }
    } catch { showToast("Failed to post comment", false); }
    finally { setPostingComment(false); }
  };

  const openDetail = async (eng: Engagement) => {
    setSelected(eng);
    setStageView(eng.engagement_stage); // initialize to current stage
    setDetailLoading(true);
    setShowIntForm(false);
    setShowAttachForm(false);
    setAttachments([]);
    setComments([]);
    setNewComment("");
    try {
      const res = await axios.get(`${STARTUP_API_URL}/api/v1/engagements/${eng.id}`, { headers });
      if (res.data.status === "success") setSelected(res.data.data);
    } finally {
      setDetailLoading(false);
    }
    fetchAttachments(eng.id);
    fetchComments(eng.id);
  };

  // ─── Save engagement ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.company_name.trim()) { showToast("Company name is required", false); return; }
    setSaving(true);
    try {
      // Always fetch a fresh token — Firebase auto-refreshes if expired
      const freshToken = await auth.currentUser?.getIdToken() ?? localStorage.getItem("authToken") ?? "";
      const freshHeaders = { Authorization: freshToken, ...tenantHeaders() };
      if (editId) {
        await axios.put(`${STARTUP_API_URL}/api/v1/engagements/${editId}`, form, { headers: freshHeaders });
        showToast("Engagement updated successfully");
      } else {
        const res = await axios.post(`${STARTUP_API_URL}/api/v1/engagements/`, { ...form, created_by: uid }, { headers: freshHeaders });
        if (res.data.status === "exists") {
          showToast(`Already tracking "${form.company_name}" — opening it`);
          setForm(emptyForm());
          setEditId(null);
          setTab("list");
          await fetchEngagements();
          // Open the existing engagement in the detail panel
          const existing = res.data.data as Engagement;
          if (existing) openDetail(existing);
          return;
        }
        showToast("Engagement created successfully");
      }
      setForm(emptyForm());
      setEditId(null);
      setTab("list");
      fetchEngagements();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message;
      if (status === 401 || status === 403) {
        showToast("Session expired — please sign in again", false);
      } else if (msg) {
        showToast(`Save failed: ${msg}`, false);
      } else {
        showToast("Failed to save engagement. Please try again.", false);
      }
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (eng: Engagement) => {
    setForm({
      company_name: eng.company_name || "",
      sector: eng.sector || "",
      technology_area: eng.technology_area || "",
      startup_stage: eng.startup_stage || "",
      geography: eng.geography || "",
      website: eng.website || "",
      founders: eng.founders || "",
      poc_name: eng.poc_name || "",
      poc_email: eng.poc_email || "",
      poc_phone: eng.poc_phone || "",
      spoc: eng.spoc || "",
      engagement_stage: eng.engagement_stage || "identified",
      notes: eng.notes || "",
    });
    setEditId(eng.id);
    setSelected(null);
    setTab("add");
  };

  const handleAiFill = async () => {
    if (!form.company_name.trim()) { showToast("Enter a company name first", false); return; }
    setAiFilling(true);
    setAiFilledFields([]);
    try {
      const res = await axios.get(`${STARTUP_API_URL}/api/v1/engagements/ai-fill`, {
        params: { company_name: form.company_name.trim() },
        headers,
      });
      const d = res.data.data;
      const filled: string[] = [];
      const aiFields: (keyof ReturnType<typeof emptyForm>)[] = ["sector", "technology_area", "startup_stage", "geography", "website", "founders", "notes"];
      setForm(prev => {
        // Start with a clean slate for AI-filled fields, keep company_name + manual POC fields
        const next = { ...emptyForm(), company_name: prev.company_name, poc_name: prev.poc_name, poc_email: prev.poc_email, poc_phone: prev.poc_phone, spoc: prev.spoc, engagement_stage: prev.engagement_stage };
        for (const f of aiFields) {
          if (d[f]) {
            (next as Record<string, string>)[f] = d[f];
            filled.push(f);
          }
        }
        return next;
      });
      setAiFilledFields(filled);
      showToast("AI filled the form — review and adjust as needed");
    } catch {
      showToast("AI fill failed. Try again.", false);
    } finally {
      setAiFilling(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this engagement?")) return;
    try {
      await axios.delete(`${STARTUP_API_URL}/api/v1/engagements/${id}`, { headers });
      showToast("Deleted successfully");
      setSelected(null);
      fetchEngagements();
    } catch {
      showToast("Failed to delete", false);
    }
  };

  // ─── Update stage inline ──────────────────────────────────────────────────────

  const updateStage = async (id: number, stage: string) => {
    try {
      await axios.put(`${STARTUP_API_URL}/api/v1/engagements/${id}`, { engagement_stage: stage }, { headers });
      setSelected(prev => prev ? { ...prev, engagement_stage: stage } : prev);
      setEngagements(prev => prev.map(e => e.id === id ? { ...e, engagement_stage: stage } : e));
      showToast("Stage updated");
    } catch {
      showToast("Failed to update stage", false);
    }
  };

  // ─── Add interaction ─────────────────────────────────────────────────────────

  const handleAddInteraction = async () => {
    if (!selected) return;
    setAddingInt(true);
    try {
      const payload = {
        ...intForm,
        date_of_interaction: new Date(intForm.date_of_interaction).toISOString(),
      };
      await axios.post(`${STARTUP_API_URL}/api/v1/engagements/${selected.id}/interactions`, payload, { headers });
      showToast("Interaction logged");
      setIntForm(emptyInteraction());
      setShowIntForm(false);
      openDetail(selected);
    } catch {
      showToast("Failed to log interaction", false);
    } finally {
      setAddingInt(false);
    }
  };

  const deleteInteraction = async (intId: number) => {
    if (!selected) return;
    if (!confirm("Delete this interaction?")) return;
    try {
      await axios.delete(`${STARTUP_API_URL}/api/v1/engagements/${selected.id}/interactions/${intId}`, { headers });
      showToast("Interaction deleted");
      openDetail(selected);
    } catch {
      showToast("Failed to delete", false);
    }
  };

  // ─── Attachments ─────────────────────────────────────────────────────────────

  const fetchAttachments = async (engId: number) => {
    try {
      const res = await axios.get(`${STARTUP_API_URL}/api/v1/engagements/${engId}/attachments`, { headers });
      if (res.data.status === "success") setAttachments(res.data.data);
    } catch {}
  };

  const handleAddAttachment = async () => {
    if (!selected) return;
    if (attachMode === "link" && !attachName.trim()) { showToast("Name is required", false); return; }
    setUploadingAttach(true);
    try {
      if (attachMode === "link") {
        if (!attachUrl.trim()) { showToast("URL is required", false); setUploadingAttach(false); return; }
        const fd = new FormData();
        fd.append("name", attachName);
        fd.append("url", attachUrl);
        await axios.post(`${STARTUP_API_URL}/api/v1/engagements/${selected.id}/attachments/link`, fd, { headers });
      } else {
        if (!attachFile) { showToast("Select a file", false); setUploadingAttach(false); return; }
        const fd = new FormData();
        fd.append("file", attachFile);
        // name sent as filename — backend uses file.filename
        await axios.post(`${STARTUP_API_URL}/api/v1/engagements/${selected.id}/attachments/upload`, fd, { headers });
      }
      showToast("Attachment added");
      setAttachName(""); setAttachUrl(""); setAttachFile(null); setShowAttachForm(false);
      fetchAttachments(selected.id);
    } catch {
      showToast("Failed to add attachment", false);
    } finally {
      setUploadingAttach(false);
    }
  };

  const deleteAttachment = async (attId: number) => {
    if (!selected) return;
    if (!confirm("Delete this attachment?")) return;
    try {
      await axios.delete(`${STARTUP_API_URL}/api/v1/engagements/${selected.id}/attachments/${attId}`, { headers });
      showToast("Attachment deleted");
      fetchAttachments(selected.id);
    } catch {
      showToast("Failed to delete", false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  // API already filters — use engagements directly
  const filtered = engagements;
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "radial-gradient(35% 19% at 50% 0%, rgba(255,84,31,0.4) 0%, rgba(0,0,0,0) 100%), black", color: "white" }}>
      <Navbar />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl text-sm font-semibold shadow-lg transition-all ${toast.ok ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
          {toast.msg}
        </div>
      )}

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 pt-20 pb-10">
        {/* Header */}
        <div className="mb-5 text-center relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-0 top-1 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all border border-white/10"
          >
            ← Back
          </button>
          <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#fff", lineHeight: 1.3, fontFamily: "Sora, sans-serif" }}>Startup Engagement Repository</h1>
          <p className="text-gray-500 text-xs mt-1">Capture, track and manage all startup interactions.</p>
          <div className="absolute right-0 top-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF541F]/10 border border-[#FF541F]/20 text-[#FF541F] text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF541F] animate-pulse" />
            Live Tracking
          </div>
        </div>

        {/* Pipeline Stage Tabs */}
        <div className="mb-5">
          {/* All tab + stage tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setFilterStage("")}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${!filterStage ? "bg-white text-black border-white" : "bg-white/5 text-gray-400 border-white/10 hover:bg-white/10"}`}
            >
              All
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${!filterStage ? "bg-black/10 text-black" : "bg-white/10 text-gray-300"}`}>
                {engagements.length}
              </span>
            </button>
            {STAGES.map(s => {
              const count = engagements.filter(e => e.engagement_stage === s.key).length;
              const isActive = filterStage === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setFilterStage(isActive ? "" : s.key)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${isActive ? "border-[#FF541F] bg-[#FF541F]/15 text-white" : "bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white"}`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.color}`} />
                  {s.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${isActive ? "bg-[#FF541F]/30 text-orange-200" : "bg-white/10 text-gray-400"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Stage-specific card view when a stage is selected */}
          {filterStage && (() => {
            const stageEngagements = engagements.filter(e => e.engagement_stage === filterStage);
            const stageInfo = STAGES.find(s => s.key === filterStage);
            if (stageEngagements.length === 0) return (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
                <div className="text-3xl mb-2">📋</div>
                <p className="text-gray-400 text-sm font-medium">No engagements in <span className="text-white">{stageInfo?.label}</span> stage</p>
                <p className="text-gray-600 text-xs mt-1">Move engagements to this stage or add a new one</p>
              </div>
            );
            return (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${stageInfo?.color}`} />
                    <span className="text-white font-semibold text-sm">{stageInfo?.label}</span>
                    <span className="text-gray-500 text-xs">({stageEngagements.length} engagements)</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stageEngagements.map(eng => (
                    <div
                      key={eng.id}
                      onClick={() => openDetail(eng)}
                      className="p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-[#FF541F]/40 cursor-pointer transition-all group"
                    >
                      {/* Company header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                          style={{ background: `hsl(${(eng.company_name.charCodeAt(0) * 37) % 360}, 60%, 35%)` }}
                        >
                          {eng.company_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-semibold text-sm truncate group-hover:text-[#FF541F] transition-colors">{eng.company_name}</p>
                          {eng.website && (
                            <a href={eng.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                              className="text-[10px] text-gray-500 hover:text-[#FF541F] truncate block transition-colors">
                              {eng.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Stage-specific fields */}
                      <div className="space-y-1.5 mb-3">
                        {eng.sector && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 w-16 flex-shrink-0">Sector</span>
                            <span className="text-xs text-gray-300 truncate">{eng.sector}</span>
                          </div>
                        )}
                        {eng.technology_area && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 w-16 flex-shrink-0">Tech</span>
                            <span className="text-xs text-gray-300 truncate">{eng.technology_area}</span>
                          </div>
                        )}
                        {eng.geography && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 w-16 flex-shrink-0">Location</span>
                            <span className="text-xs text-gray-300 truncate">{eng.geography}</span>
                          </div>
                        )}
                        {eng.poc_name && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 w-16 flex-shrink-0">POC</span>
                            <span className="text-xs text-gray-300 truncate">{eng.poc_name}</span>
                          </div>
                        )}
                        {eng.spoc && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 w-16 flex-shrink-0">SPOC</span>
                            <span className="text-xs text-gray-300 truncate">{eng.spoc}</span>
                          </div>
                        )}
                        {eng.startup_stage && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 w-16 flex-shrink-0">Stage</span>
                            <span className="text-xs bg-white/10 text-gray-300 px-1.5 py-0.5 rounded">{normalizeStartupStage(eng.startup_stage)}</span>
                          </div>
                        )}
                      </div>

                      {/* Notes preview */}
                      {eng.notes && (
                        <p className="text-[11px] text-gray-500 line-clamp-2 mb-3 leading-relaxed">{eng.notes}</p>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 border-t border-white/10">
                        <span className="text-[10px] text-gray-600">
                          Updated {new Date(eng.updated_at).toLocaleDateString()}
                        </span>
                        <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => openEdit(eng)}
                            className="text-[10px] px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 text-gray-400 transition-all"
                          >Edit</button>
                          <button
                            onClick={() => handleDelete(eng.id)}
                            className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                          >Del</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── SEARCH BAR ─────────────────────────────────────────────────────── */}
        <div className="mb-5">
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === "Enter" && fetchEngagements()}
                placeholder="Search by company name, sector, technology area..."
                className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#FF541F]/60"
              />
            </div>
            <button
              onClick={() => fetchEngagements()}
              className="px-5 py-2.5 rounded-xl bg-[#FF541F] text-white text-sm font-semibold hover:bg-[#e04a1a] transition-all"
            >
              Search
            </button>
          </div>
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2">
            <select
              value={filterStage}
              onChange={e => setFilterStage(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-xs focus:outline-none hover:border-white/20"
            >
              <option value="">All Engagement Stages</option>
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select
              value={filterSector}
              onChange={e => setFilterSector(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-xs focus:outline-none hover:border-white/20"
            >
              <option value="">All Sectors</option>
              {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={filterGeo}
              onChange={e => setFilterGeo(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-xs focus:outline-none hover:border-white/20"
            >
              <option value="">All Geographies</option>
              {[...new Set([...GEOGRAPHIES, ...engagements.map(e => e.geography).filter(Boolean)])].sort().map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            {(search || filterStage || filterSector || filterGeo) && (
              <button
                onClick={() => { setSearch(""); setFilterStage(""); setFilterSector(""); setFilterGeo(""); }}
                className="px-3 py-1.5 rounded-lg bg-white/10 text-gray-400 text-xs hover:bg-white/20 transition-all"
              >
                ✕ Clear filters
              </button>
            )}
            <span className="ml-auto text-xs text-gray-500 self-center">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
          </div>
        </div>

        {/* Top bar — tabs + add button */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            onClick={() => { setTab("list"); setEditId(null); setForm(emptyForm()); }}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${tab === "list" ? "bg-[#FF541F] text-white" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}
          >
            All Engagements ({engagements.length})
          </button>
          <button
            onClick={() => { setTab("add"); setEditId(null); setForm(emptyForm()); setSelected(null); }}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${tab === "add" ? "bg-[#FF541F] text-white" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}
          >
            + Add Engagement
          </button>
        </div>

        {/* ── LIST TAB ─────────────────────────────────────────────────────── */}
        {tab === "list" && (
          <div className="flex gap-6">
            {/* Left: table */}
            <div className="flex-1 min-w-0">

              {/* Table */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="relative w-10 h-10">
                    <div className="w-10 h-10 rounded-full absolute border-4 border-solid border-white/10" />
                    <div className="w-10 h-10 rounded-full animate-spin absolute border-4 border-solid border-[#FF541F] border-t-transparent" />
                  </div>
                  <span className="text-gray-500 text-sm">Loading engagements...</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-gray-500 border border-white/10 rounded-2xl bg-white/[0.02]">
                  <div className="text-4xl mb-3">📋</div>
                  <p className="text-base font-medium text-gray-400 mb-1">No engagements found</p>
                  <p className="text-sm text-gray-600 mb-4">
                    {search || filterStage || filterSector || filterGeo ? "Try adjusting your filters." : "Start tracking your startup interactions."}
                  </p>
                  {!(search || filterStage || filterSector || filterGeo) && (
                    <button onClick={() => setTab("add")} className="px-5 py-2 rounded-lg bg-[#FF541F] text-white text-sm font-semibold hover:bg-[#e04a1a] transition-all">
                      + Add your first engagement
                    </button>
                  )}
                </div>
              ) : (
                <>
                <div className="rounded-2xl border border-white/10 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead style={{ background: "rgba(255,255,255,0.04)" }}>
                      <tr className="text-gray-500 text-xs uppercase tracking-wider">
                        <th className="px-4 py-3.5 text-left font-semibold">Company</th>
                        <th className="px-4 py-3.5 text-left font-semibold">Sector</th>
                        <th className="px-4 py-3.5 text-left font-semibold">Stage / Status</th>
                        <th className="px-4 py-3.5 text-left font-semibold">SPOC</th>
                        <th className="px-4 py-3.5 text-left font-semibold">Updated</th>
                        <th className="px-4 py-3.5 text-left font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((eng) => (
                        <tr
                          key={eng.id}
                          onClick={() => openDetail(eng)}
                          className={`border-t border-white/5 cursor-pointer transition-all hover:bg-white/[0.06] ${selected?.id === eng.id ? "bg-[#FF541F]/5 border-l-2 border-l-[#FF541F]" : ""}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                                style={{ background: `hsl(${(eng.company_name.charCodeAt(0) * 37) % 360}, 60%, 35%)` }}>
                                {eng.company_name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-semibold text-white leading-tight">
                                  {eng.company_name}
                                  {eng.website && (
                                    <a href={eng.website} target="_blank" rel="noreferrer"
                                      onClick={e => e.stopPropagation()}
                                      className="ml-1.5 text-[#FF541F] text-xs hover:underline">↗</a>
                                  )}
                                </div>
                                {eng.technology_area && <div className="text-gray-500 text-xs mt-0.5">{eng.technology_area}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-gray-300 text-sm">{eng.sector || "—"}</div>
                            {eng.geography && <div className="text-gray-600 text-xs mt-0.5">{eng.geography}</div>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5">
                              {eng.startup_stage ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-gray-300 w-fit">
                                  {normalizeStartupStage(eng.startup_stage)}
                                </span>
                              ) : (
                                <span className="text-gray-600 text-xs">—</span>
                              )}
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] text-white font-medium w-fit ${stageColor(eng.engagement_stage)}`}>
                                <span className="w-1 h-1 rounded-full bg-white/50" />
                                {stageLabel(eng.engagement_stage)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {eng.spoc ? (
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-bold flex-shrink-0">
                                  {eng.spoc.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-gray-300 text-sm">{eng.spoc}</span>
                              </div>
                            ) : <span className="text-gray-600">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {new Date(eng.updated_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                              <button onClick={() => openEdit(eng)} className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-gray-300">Edit</button>
                              <button onClick={() => handleDelete(eng.id)} className="text-xs px-2 py-1 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400">Del</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 mt-1">
                    <span className="text-xs text-gray-500">
                      Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-2.5 py-1 rounded-lg text-xs bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >← Prev</button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`w-7 h-7 rounded-lg text-xs font-medium transition ${p === currentPage ? "bg-orange-500 text-white" : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"}`}
                        >{p}</button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-2.5 py-1 rounded-lg text-xs bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >Next →</button>
                    </div>
                  </div>
                )}
                </>
              )}
            </div>

            {/* Full-page detail overlay */}
            {selected && (
              <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: "radial-gradient(35% 19% at 50% 0%, rgba(255,84,31,0.4) 0%, rgba(0,0,0,0) 100%), #000" }}>
              <div className="max-w-4xl mx-auto px-4 py-8">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                {detailLoading ? (
                  <div className="text-gray-400 text-sm text-center py-8">Loading...</div>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-5">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                          style={{ background: `hsl(${(selected.company_name.charCodeAt(0) * 37) % 360}, 60%, 35%)` }}>
                          {selected.company_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-white leading-tight">{selected.company_name}</h2>
                          {selected.website ? (
                            <a href={selected.website} target="_blank" rel="noreferrer" className="text-[#FF541F] text-xs hover:underline">
                              {selected.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                            </a>
                          ) : selected.geography ? (
                            <span className="text-gray-500 text-xs">{selected.geography}</span>
                          ) : null}
                        </div>
                      </div>
                      <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all border border-white/10">← Back</button>
                    </div>

                    {/* Engagement Stage — tabbed view */}
                    {(() => {
                      const STAGE_INFO: Record<string, { icon: string; description: string; checklist: string[]; nextAction: string; suggestedActivities: { icon: string; label: string }[] }> = {
                        identified: {
                          icon: "🔍",
                          description: "Startup identified as a potential partner. Initial research completed.",
                          checklist: ["Added to engagement repository", "Initial research done", "Relevance to our needs verified", "Founder / team background checked"],
                          nextAction: "Schedule an intro call to make first contact",
                          suggestedActivities: [
                            { icon: "📋", label: "Review founder & team background" },
                            { icon: "💰", label: "Check recent funding rounds" },
                            { icon: "🔗", label: "Visit website & LinkedIn profile" },
                            { icon: "🗂️", label: "Map to internal use case or initiative" },
                          ],
                        },
                        connected: {
                          icon: "🤝",
                          description: "First contact made. Building relationship and understanding their offering.",
                          checklist: ["Intro call completed", "POC / key contact captured", "Startup briefed on our requirements", "Follow-up email sent"],
                          nextAction: "Request a product demo or detailed evaluation",
                          suggestedActivities: [
                            { icon: "📞", label: "Schedule introductory call" },
                            { icon: "📧", label: "Send company overview / brief" },
                            { icon: "👤", label: "Capture key contact details" },
                            { icon: "📅", label: "Book follow-up meeting" },
                          ],
                        },
                        evaluated: {
                          icon: "🔬",
                          description: "Evaluating technology fit, business case and POC viability.",
                          checklist: ["Product demo completed", "Technical assessment done", "Business case reviewed", "Fit vs our use-case confirmed"],
                          nextAction: "Decide: move to POC or drop",
                          suggestedActivities: [
                            { icon: "🎥", label: "Request a product demo" },
                            { icon: "🛠️", label: "Conduct technical due diligence" },
                            { icon: "📊", label: "Build internal business case" },
                            { icon: "📝", label: "Share evaluation report with stakeholders" },
                          ],
                        },
                        poc: {
                          icon: "🧪",
                          description: "Proof of Concept in progress. Track outcomes and success criteria.",
                          checklist: ["POC scope & success criteria defined", "POC kicked off with team", "Progress being tracked regularly", "Issues / blockers documented"],
                          nextAction: "Evaluate POC results and decide next steps",
                          suggestedActivities: [
                            { icon: "🎯", label: "Define success criteria & timeline" },
                            { icon: "👥", label: "Assign internal team lead" },
                            { icon: "🔄", label: "Set weekly sync cadence" },
                            { icon: "⚠️", label: "Document blockers & learnings" },
                          ],
                        },
                        active_collaboration: {
                          icon: "🚀",
                          description: "Active partnership in place. Track milestones and deliverables.",
                          checklist: ["Agreement / MOU in place", "Internal team aligned", "Milestones & KPIs defined", "Regular review cadence set"],
                          nextAction: "Track ongoing progress and ensure deliverables are on track",
                          suggestedActivities: [
                            { icon: "📄", label: "Finalise MOU or agreement" },
                            { icon: "📌", label: "Define KPIs & delivery milestones" },
                            { icon: "🧑‍💼", label: "Schedule executive sponsor review" },
                            { icon: "🚨", label: "Establish escalation process" },
                          ],
                        },
                      };

                      const currentStageInfo = STAGE_INFO[stageView] || STAGE_INFO["identified"];
                      const isCurrentStage = stageView === selected.engagement_stage;

                      return (
                        <div className="mb-5">
                          <p className="text-xs text-gray-400 mb-2">Engagement Stage</p>

                          {/* Stage tabs */}
                          <div className="flex flex-wrap gap-1 mb-4">
                            {STAGES.map(s => {
                              const isActive = stageView === s.key;
                              const isCurrent = selected.engagement_stage === s.key;
                              return (
                                <button
                                  key={s.key}
                                  onClick={() => setStageView(s.key)}
                                  className={`relative px-3 py-1.5 rounded-full text-xs font-medium transition-all ${isActive ? `${s.color} text-white shadow-lg` : "bg-white/10 text-gray-400 hover:bg-white/20"}`}
                                >
                                  {s.label}
                                  {isCurrent && (
                                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-black" title="Current stage" />
                                  )}
                                </button>
                              );
                            })}
                          </div>

                          {/* Stage content panel */}
                          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{currentStageInfo.icon}</span>
                                <div>
                                  <p className="text-sm font-semibold text-white">{STAGES.find(s => s.key === stageView)?.label}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">{currentStageInfo.description}</p>
                                </div>
                              </div>
                              {/* Move to this stage button — only show if not already at this stage */}
                              {!isCurrentStage && (
                                <button
                                  onClick={() => { updateStage(selected.id, stageView); setStageView(stageView); }}
                                  className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${STAGES.find(s => s.key === stageView)?.color} text-white hover:opacity-80`}
                                >
                                  Move here →
                                </button>
                              )}
                              {isCurrentStage && (
                                <span className="flex-shrink-0 flex items-center gap-1 text-xs text-green-400 bg-green-400/10 border border-green-400/20 px-2.5 py-1 rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                                  Current Stage
                                </span>
                              )}
                            </div>

                            {/* Suggested Activities heading */}
                            <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Suggested Activities</p>

                            {/* Checklist */}
                            <div className="space-y-1.5 mb-3">
                              {currentStageInfo.checklist.map((item, i) => {
                                const checklistKey = `${selected.id}_${stageView}`;
                                const isChecked = (checkedItems[checklistKey] || []).includes(i);
                                return (
                                  <div
                                    key={i}
                                    className="flex items-center gap-2 cursor-pointer group"
                                    onClick={() => setCheckedItems(prev => {
                                      const current = prev[checklistKey] || [];
                                      return {
                                        ...prev,
                                        [checklistKey]: current.includes(i)
                                          ? current.filter(x => x !== i)
                                          : [...current, i],
                                      };
                                    })}
                                  >
                                    <span className="w-4 h-4 rounded-full border border-white/20 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:border-green-400/50 transition-colors">
                                      {isChecked
                                        ? <span className="w-2 h-2 rounded-full bg-green-400" />
                                        : <span className="w-2 h-2 rounded-full bg-white/20" />}
                                    </span>
                                    <span className={`text-xs transition-colors ${isChecked ? "text-green-400 line-through" : "text-gray-400 group-hover:text-gray-300"}`}>{item}</span>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Next action */}
                            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-[#FF541F]/10 border border-[#FF541F]/20">
                              <span className="text-[#FF541F] text-xs mt-0.5">→</span>
                              <p className="text-xs text-[#FF541F] font-medium">{currentStageInfo.nextAction}</p>
                            </div>
                          </div>

                        </div>
                      );
                    })()}

                    {/* Info grid */}
                    <div className="grid grid-cols-2 gap-3 mb-5 text-xs">
                      {[
                        ["Sector", selected.sector],
                        ["Technology", selected.technology_area],
                        ["Stage", selected.startup_stage],
                        ["Geography", selected.geography],
                        ["Founders", selected.founders],
                        ["SPOC (Our Team)", selected.spoc],
                      ].map(([label, val]) => val ? (
                        <div key={label as string}>
                          <p className="text-gray-500">{label}</p>
                          <p className="text-white font-medium">{val}</p>
                        </div>
                      ) : null)}
                    </div>

                    {/* POC */}
                    {(selected.poc_name || selected.poc_email || selected.poc_phone) && (
                      <div className="mb-5 p-3 rounded-xl bg-white/5 border border-white/10">
                        <p className="text-xs text-gray-400 mb-2 font-semibold">Primary POC</p>
                        {selected.poc_name && <p className="text-white text-sm font-medium">{selected.poc_name}</p>}
                        {selected.poc_email && <p className="text-gray-400 text-xs">{selected.poc_email}</p>}
                        {selected.poc_phone && <p className="text-gray-400 text-xs">{selected.poc_phone}</p>}
                      </div>
                    )}

                    {/* Notes */}
                    {selected.notes && (
                      <div className="mb-5">
                        <p className="text-xs text-gray-500 mb-1">Notes</p>
                        <p className="text-gray-300 text-xs whitespace-pre-wrap">{selected.notes}</p>
                      </div>
                    )}

                    {/* Interactions */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <p className="text-sm font-semibold text-white">Interaction History</p>
                        <button
                          onClick={() => setShowIntForm(v => !v)}
                          className="text-xs px-3 py-1 rounded-lg bg-[#FF541F]/20 text-[#FF541F] hover:bg-[#FF541F]/30 transition-all"
                        >
                          + Log
                        </button>
                      </div>

                      {showIntForm && (
                        <div className="mb-4 p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-gray-400">Date & Time</label>
                              <input
                                type="datetime-local"
                                value={intForm.date_of_interaction}
                                onChange={e => setIntForm(p => ({ ...p, date_of_interaction: e.target.value }))}
                                className="w-full mt-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-400">Type</label>
                              <select
                                value={intForm.interaction_type}
                                onChange={e => setIntForm(p => ({ ...p, interaction_type: e.target.value }))}
                                className="w-full mt-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-xs focus:outline-none"
                              >
                                {INTERACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-400">SPOC (Who interacted)</label>
                            <input
                              value={intForm.spoc}
                              onChange={e => setIntForm(p => ({ ...p, spoc: e.target.value }))}
                              placeholder="Team member name"
                              className="w-full mt-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400">Notes / Key Insights</label>
                            <textarea
                              value={intForm.notes}
                              onChange={e => setIntForm(p => ({ ...p, notes: e.target.value }))}
                              rows={2}
                              placeholder="Key outcomes, next steps..."
                              className="w-full mt-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:outline-none resize-none"
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setShowIntForm(false)} className="text-xs px-3 py-1.5 rounded-lg bg-white/10 text-gray-300 hover:bg-white/20">Cancel</button>
                            <button onClick={handleAddInteraction} disabled={addingInt} className="text-xs px-3 py-1.5 rounded-lg bg-[#FF541F] text-white hover:bg-[#e04a1a] disabled:opacity-50">
                              {addingInt ? "Saving..." : "Log Interaction"}
                            </button>
                          </div>
                        </div>
                      )}

                      {selected.interactions?.length === 0 && (
                        <p className="text-xs text-gray-500">No interactions logged yet.</p>
                      )}
                      <div className="space-y-2">
                        {(selected.interactions || []).map(int => (
                          <div key={int.id} className="p-3 rounded-xl bg-white/5 border border-white/10 text-xs">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-semibold text-[#FF541F]">{int.interaction_type}</span>
                                {int.spoc && <span className="text-gray-400 ml-2">by {int.spoc}</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">{new Date(int.date_of_interaction).toLocaleDateString()}</span>
                                <button onClick={() => deleteInteraction(int.id)} className="text-red-400 hover:text-red-300 text-xs">×</button>
                              </div>
                            </div>
                            {int.notes && <p className="text-gray-400 mt-1 whitespace-pre-wrap">{int.notes}</p>}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Team Notes (shared comment thread) */}
                    <div className="mt-5 pt-4 border-t border-white/10">
                      <p className="text-sm font-semibold text-white mb-3">Team Notes</p>
                      <div className="mb-3 max-h-64 overflow-y-auto space-y-2">
                        {comments.length === 0 ? (
                          <p className="text-xs text-gray-500 py-2">No team notes yet. Be the first to add one.</p>
                        ) : comments.map((c, i) => (
                          <div key={c.id} className={`flex gap-2.5 ${i > 0 ? "ml-6 pl-3 border-l-2 border-orange-500/20" : ""}`}>
                            <div className="w-6 h-6 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-orange-300 text-[10px] font-bold flex-shrink-0 mt-0.5">
                              {c.user_email.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0 bg-white/5 rounded-xl px-3 py-2 border border-white/10">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-white">{c.user_email.split("@")[0]}</span>
                                <span className="text-[10px] text-gray-500">{new Date(c.created_at).toLocaleDateString()} · {new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                              </div>
                              <p className="text-xs text-gray-400 whitespace-pre-wrap leading-relaxed">{c.comment}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <textarea
                          value={newComment}
                          onChange={e => setNewComment(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handlePostComment(); }}
                          placeholder="Add your notes / observations..."
                          rows={2}
                          className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:outline-none focus:border-orange-500/50 resize-none"
                        />
                        <button
                          onClick={handlePostComment}
                          disabled={postingComment || !newComment.trim()}
                          className="px-3 py-2 rounded-lg bg-[#FF541F] text-white text-xs font-medium hover:bg-[#e04a1a] disabled:opacity-40 self-end"
                        >
                          {postingComment ? "..." : "Post"}
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-600 mt-1">Ctrl+Enter to post</p>
                    </div>

                    {/* Attachments */}
                    <div className="mt-5 pt-4 border-t border-white/10">
                      <div className="flex justify-between items-center mb-3">
                        <p className="text-sm font-semibold text-white">Attachments</p>
                        <button
                          onClick={() => setShowAttachForm(v => !v)}
                          className="text-xs px-3 py-1 rounded-lg bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 transition-all"
                        >
                          + Add
                        </button>
                      </div>

                      {showAttachForm && (
                        <div className="mb-4 p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
                          <div className="flex gap-2">
                            {(["link", "file"] as const).map(m => (
                              <button key={m} onClick={() => setAttachMode(m)}
                                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${attachMode === m ? "bg-indigo-500 text-white" : "bg-white/10 text-gray-400 hover:bg-white/20"}`}>
                                {m === "link" ? "🔗 Link" : "📎 Upload File"}
                              </button>
                            ))}
                          </div>
                          <div>
                            <label className="text-xs text-gray-400">Name</label>
                            <input value={attachName} onChange={e => setAttachName(e.target.value)}
                              placeholder={attachMode === "link" ? "e.g. Pitch Deck Q1 2025" : "e.g. Company Brochure"}
                              className="w-full mt-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:outline-none" />
                          </div>
                          {attachMode === "link" ? (
                            <div>
                              <label className="text-xs text-gray-400">URL (Google Drive, Dropbox, etc.)</label>
                              <input value={attachUrl} onChange={e => setAttachUrl(e.target.value)}
                                placeholder="https://drive.google.com/..."
                                className="w-full mt-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:outline-none" />
                            </div>
                          ) : (
                            <div>
                              <label className="text-xs text-gray-400">File (PDF, PPTX, DOCX, XLSX — max 10 MB)</label>
                              <input ref={fileInputRef} type="file"
                                accept=".pdf,.pptx,.ppt,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg"
                                onChange={e => {
                                const f = e.target.files?.[0] || null;
                                setAttachFile(f);
                                if (f && !attachName.trim()) setAttachName(f.name);
                              }}
                                className="w-full mt-1 text-xs text-gray-400 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-500/20 file:text-indigo-300 file:text-xs cursor-pointer" />
                            </div>
                          )}
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => { setShowAttachForm(false); setAttachName(""); setAttachUrl(""); setAttachFile(null); }}
                              className="text-xs px-3 py-1.5 rounded-lg bg-white/10 text-gray-300 hover:bg-white/20">Cancel</button>
                            <button onClick={handleAddAttachment} disabled={uploadingAttach}
                              className="text-xs px-3 py-1.5 rounded-lg bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-50">
                              {uploadingAttach ? "Saving..." : "Add"}
                            </button>
                          </div>
                        </div>
                      )}

                      {attachments.length === 0 && !showAttachForm && (
                        <p className="text-xs text-gray-600">No attachments yet — add pitch decks, documents or links.</p>
                      )}

                      <div className="space-y-2">
                        {attachments.map(att => {
                          const isFile = att.attachment_type === "file";
                          const ext = att.name.split(".").pop()?.toLowerCase() || "";
                          const icon = ext === "pdf" ? "📄" : ["ppt","pptx"].includes(ext) ? "📊" : ["doc","docx"].includes(ext) ? "📝" : ["xls","xlsx"].includes(ext) ? "📈" : isFile ? "📎" : "🔗";
                          const href = isFile ? `${STARTUP_API_URL}${att.url}` : att.url;
                          return (
                            <div key={att.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 border border-white/10 group">
                              <span className="text-lg flex-shrink-0">{icon}</span>
                              <div className="flex-1 min-w-0">
                                <a href={href} target="_blank" rel="noreferrer"
                                  className="text-xs font-medium text-white hover:text-indigo-300 transition-colors truncate block">
                                  {att.name}
                                </a>
                                <span className="text-gray-600 text-[10px]">{isFile ? "Uploaded" : "Link"} · {new Date(att.created_at).toLocaleDateString()}</span>
                              </div>
                              <button onClick={() => deleteAttachment(att.id)}
                                className="text-gray-600 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">×</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-5 pt-4 border-t border-white/10">
                      <button onClick={() => openEdit(selected)} className="flex-1 py-2 rounded-lg bg-white/10 text-gray-300 text-xs hover:bg-white/20 transition-all">Edit Details</button>
                      <button onClick={() => handleDelete(selected.id)} className="flex-1 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 transition-all">Delete</button>
                    </div>
                  </>
                )}
              </div>
              </div>
              </div>
            )}
          </div>
        )}

        {/* ── ADD / EDIT TAB ───────────────────────────────────────────────── */}
        {tab === "add" && (
          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8">
              <h2 className="text-xl font-bold text-white mb-6">{editId ? "Edit Engagement" : "Add New Engagement"}</h2>

              {/* AI Fill Banner */}
              {!editId && (
                <div className="flex items-center justify-between mb-5 px-4 py-3 rounded-xl bg-orange-500/10 border border-orange-500/20">
                  <div>
                    <p className="text-sm font-medium text-orange-300">AI Auto-Fill</p>
                    <p className="text-xs text-gray-400 mt-0.5">Enter a company name below and let AI fill the rest</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAiFill}
                    disabled={aiFilling}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition disabled:opacity-50"
                  >
                    {aiFilling ? (
                      <><span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />Filling...</>
                    ) : (
                      <><span>✦</span> Fill for me</>
                    )}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Company Name */}
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">Company Name *</label>
                  <input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
                    placeholder="e.g. Arali Ventures"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF541F]/50" />
                </div>

                {/* Sector */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Sector / Industry {aiFilledFields.includes("sector") && <span className="text-orange-400 ml-1">✦ AI</span>}</label>
                  <select value={form.sector} onChange={e => setForm(p => ({ ...p, sector: e.target.value }))}
                    className={`w-full px-4 py-2.5 rounded-lg bg-white/5 border text-gray-300 text-sm focus:outline-none ${aiFilledFields.includes("sector") ? "border-orange-500/50" : "border-white/10"}`}>
                    <option value="">Select sector</option>
                    {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {/* Tech area */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Technology Area {aiFilledFields.includes("technology_area") && <span className="text-orange-400 ml-1">✦ AI</span>}</label>
                  <input value={form.technology_area} onChange={e => setForm(p => ({ ...p, technology_area: e.target.value }))}
                    placeholder="e.g. GenAI, IoT, Blockchain"
                    className={`w-full px-4 py-2.5 rounded-lg bg-white/5 border text-white text-sm placeholder-gray-600 focus:outline-none ${aiFilledFields.includes("technology_area") ? "border-orange-500/50" : "border-white/10 focus:border-[#FF541F]/50"}`} />
                </div>

                {/* Startup Stage */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Stage</label>
                  <select value={form.startup_stage} onChange={e => setForm(p => ({ ...p, startup_stage: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-sm focus:outline-none">
                    <option value="">Select stage</option>
                    {STARTUP_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {/* Geography */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Geography</label>
                  <input value={form.geography} onChange={e => setForm(p => ({ ...p, geography: e.target.value }))}
                    placeholder="e.g. India, Southeast Asia"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF541F]/50" />
                </div>

                {/* Website */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Website / Profile Link</label>
                  <input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
                    placeholder="https://..."
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF541F]/50" />
                </div>

                {/* Founders */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Founders & Team</label>
                  <input value={form.founders} onChange={e => setForm(p => ({ ...p, founders: e.target.value }))}
                    placeholder="John Doe, Jane Smith"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF541F]/50" />
                </div>

                {/* Divider — Startup Contact */}
                <div className="md:col-span-2">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest border-t border-white/10 pt-4">Startup Contact</p>
                  <p className="text-xs text-gray-600 mt-0.5">Point of contact at the startup</p>
                </div>

                {/* POC Name */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Contact Person Name</label>
                  <input value={form.poc_name} onChange={e => setForm(p => ({ ...p, poc_name: e.target.value }))}
                    placeholder="e.g. Ravi Kumar (CEO)"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF541F]/50" />
                </div>

                {/* POC Email */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Contact Email</label>
                  <input type="email" value={form.poc_email} onChange={e => setForm(p => ({ ...p, poc_email: e.target.value }))}
                    placeholder="contact@startup.com"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF541F]/50" />
                </div>

                {/* POC Phone */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Contact Phone</label>
                  <input value={form.poc_phone} onChange={e => setForm(p => ({ ...p, poc_phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF541F]/50" />
                </div>

                {/* Divider — Account Manager */}
                <div className="md:col-span-2">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest border-t border-white/10 pt-4">Account Manager</p>
                  <p className="text-xs text-gray-600 mt-0.5">Team member managing this engagement from our side</p>
                </div>

                {/* SPOC → Account Manager */}
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">Account Manager / SPOC</label>
                  <input value={form.spoc} onChange={e => setForm(p => ({ ...p, spoc: e.target.value }))}
                    placeholder="e.g. Anita Singh — Business Development"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF541F]/50" />
                </div>

                {/* Engagement Stage */}
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-400 mb-2">Engagement Stage</label>
                  <div className="flex flex-wrap gap-2">
                    {STAGES.map(s => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setForm(p => ({ ...p, engagement_stage: s.key }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${form.engagement_stage === s.key ? `${s.color} text-white` : "bg-white/10 text-gray-400 hover:bg-white/20"}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">Notes / Key Insights {aiFilledFields.includes("notes") && <span className="text-orange-400 ml-1">✦ AI suggested</span>}</label>
                  <textarea
                    value={form.notes}
                    onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                    rows={3}
                    placeholder="Key insights from discussion, next steps, attachments, pitch deck links..."
                    className={`w-full px-4 py-2.5 rounded-lg bg-white/5 border text-white text-sm placeholder-gray-600 focus:outline-none resize-none ${aiFilledFields.includes("notes") ? "border-orange-500/50" : "border-white/10 focus:border-[#FF541F]/50"}`}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6 justify-end">
                <button
                  onClick={() => { setTab("list"); setEditId(null); setForm(emptyForm()); }}
                  className="px-6 py-2.5 rounded-lg bg-white/10 text-gray-300 text-sm hover:bg-white/20 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-8 py-2.5 rounded-lg bg-[#FF541F] text-white text-sm font-semibold hover:bg-[#e04a1a] disabled:opacity-50 transition-all"
                >
                  {saving ? "Saving..." : editId ? "Update Engagement" : "Save Engagement"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
      <AgentChat createdBy={localStorage.getItem("uid") || ""} onDataChanged={() => fetchEngagements()} />
    </div>
  );
}
